import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../../common/utils';
import { sendTrackEvent } from '../../../../common/utils/analytics-client';
import { captureCallerSignals } from '../../../../common/utils/caller-attribution';
import {
	isProcessingAllowedByPreferencesMaybeSync,
	isPromise,
} from '../../../../common/utils/item-processing';
import { Logger } from '../../../../common/utils/logger';
import {
	shouldReportUncategorizedItemSet,
	uncategorizedItemSetOnSent,
} from '../../../../services/uncategorized-item-report-cache';
import { CategoryStorageType } from '../../../../types';

export const getSetCookieOverride =
	(originalSetter: (v: any) => any) =>
	(originalCookieValue: string): any => {
		const cookieSetByPackage = originalCookieValue.includes('atl-set-cookie');
		if (cookieSetByPackage) {
			try {
				// Controls have already been run, passthrough as usual
				return originalSetter.apply(document, [originalCookieValue]);
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'usedDocumentCookieError',
					message: `document.cookie setter failed to set cookie set by package. ${e.message || ''}`,
					attributes: {
						outsidePackage: false,
					},
				});
			}
		}

		const extensionSignals = fg('platform_bsc_browser_extension_detection')
			? captureCallerSignals()
			: undefined;

		// Set cookie function reports failure by throwing and callers such as the availability probes
		// depend on that, so a synchronous write failure has to leave this override. Collected here
		// rather than thrown inline, because the handler that catches it sits inside
		// another handler that would report the same error again.
		let setCookieFailure: any;
		try {
			// Raw cookies and query params can contain sensitive PII/UGC we can't store, so we want to
			// strip that out before we log it out to our analytics client.
			const sanitizedCookieKey =
				originalCookieValue.slice(0, originalCookieValue.indexOf('=')) || '';

			sendPackageOperationalEvent({
				action: 'usedDocumentCookie',
				attributes: {
					cookieKey: sanitizedCookieKey,
					outsidePackage: !cookieSetByPackage,
				},
			});
			try {
				const productName = getBscGlobalState().product;

				// MaybeSync skips the promise entirely once categories and preferences are loaded, which
				// is what keeps the write and any throw from it synchronous.
				// Attempt to set a known cookie that was set from outside the package
				const processingCheck = isProcessingAllowedByPreferencesMaybeSync({
					itemKey: sanitizedCookieKey,
					allowedCallback: () => {
						return originalSetter.apply(document, [originalCookieValue]);
					},
					blockedCallback: ({ itemHasCategory, itemCategory }) => {
						if (
							!itemHasCategory &&
							shouldReportUncategorizedItemSet(CategoryStorageType.Cookies, sanitizedCookieKey)
						) {
							sendTrackEvent({
								source: 'package',
								action: 'package',
								actionSubject: 'uncategorizedWebStorageItemSet',
								attributes: {
									storageType: CategoryStorageType.Cookies,
									productName,
									originalItemKey: sanitizedCookieKey,
									itemCategory: itemCategory ?? null,
									cacheLoadedDate: getBscGlobalState().cacheLoadedDate ?? null,
								},
								onSent: uncategorizedItemSetOnSent(CategoryStorageType.Cookies, sanitizedCookieKey),
							});
						}

						const { allowUncategorizedCookiesInOverrides } = getBscGlobalState();

						if (allowUncategorizedCookiesInOverrides && !itemHasCategory) {
							return originalSetter.apply(document, [originalCookieValue]);
						} else {
							if (!itemHasCategory) {
								Logger.warn(
									`document.cookie setter for '${sanitizedCookieKey}' was blocked` +
										`${productName ? ` for product "${productName}"` : ''}` +
										`: '${sanitizedCookieKey}' is not registered in the BSC data store. Add it to the data store before calling this API.`,
								);
							}
							return;
						}
					},
					storageType: CategoryStorageType.Cookies,
					productName,
					extensionSignals,
				});
				if (isPromise<boolean>(processingCheck)) {
					processingCheck.catch((e: any) => {
						Logger.errorWithOperationalEvent({
							action: 'usedDocumentCookieError',
							message: `Failed to set cookie. ${e?.message || ''}`,
							attributes: {
								cookieKey: sanitizedCookieKey,
							},
						});
					});
				}
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'usedDocumentCookieError',
					message: `Failed to set cookie. ${e.message || ''}`,
					attributes: {
						cookieKey: sanitizedCookieKey,
					},
				});
				setCookieFailure = e;
			}
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedDocumentCookieError',
				message: `Failed to send document.cookie setter event. ${e.message || ''}`,
			});
		}

		if (setCookieFailure) {
			throw setCookieFailure;
		}
	};
